import React, {Component, ErrorInfo, ReactNode} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {withRouter} from 'react-router-dom';
import {Alert, AlertTitle} from '@material-ui/lab';
import styled from 'styled-components';
import Layout from './Layout';
import UserInfoProvider from 'hooks/useUserInfo/UserInfoProvider';
import SiteStateProvider from 'hooks/useSiteInfo/SiteStateProvider';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
}

const StyledBoundary = styled.div`
  height: 100%;
  min-height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  .MuiAlert-root {
    width: calc(100% - 48px);

    @media (min-width: 600px) {
      width: calc(100% - 96px);
    }
  }
`;

interface Props extends RouteComponentProps {
  errorMessage: null | string;
}

interface BoundaryState extends State {
  errorMessage: null | string;
}

class ErrorBoundary extends Component<Props, BoundaryState> {
  state = {
    hasError: false,
    errorMessage: null,
  };

  constructor(props: Props) {
    super(props);
    this.resetErrorBoundary = this.resetErrorBoundary.bind(this);
  }

  static getDerivedStateFromError(error: Error): BoundaryState {
    // Update state so the next render will show the fallback UI.
    return {hasError: true, errorMessage: error?.message ?? 'Something went wrong'};
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // TODO: Save the log in a long term storage
    // console.log("Uncaught error:", error?.message, errorInfo);
  }

  componentDidUpdate(prevProps: Props) {
    // If route change the app will recover control and error state is reset
    if (this.state.hasError && this.props.location !== prevProps.location) {
      this.resetErrorBoundary();
    }
  }

  resetErrorBoundary() {
    this.setState({hasError: false});
  }

  render() {
    if (this.state.hasError) {
      return (
        <UserInfoProvider>
          <SiteStateProvider>
            <Layout>
              <StyledBoundary>
                <Alert severity="warning">
                  <AlertTitle>
                    <strong>Warning</strong>
                  </AlertTitle>
                  {this.state.errorMessage}
                </Alert>
              </StyledBoundary>
            </Layout>
          </SiteStateProvider>
        </UserInfoProvider>
      );
    }

    return this.props.children;
  }
}

export default withRouter(ErrorBoundary);
